import { computed, type WritableComputedRef } from 'vue'

/**
 * The value a „nothing chosen“ option carries — „—“, „brez oddelka“, „privzeto“.
 *
 * # Why it cannot be the empty string
 *
 * Reka UI (2.10.1, under `@nuxt/ui` 4.10.0) THROWS out of `<SelectItem>` and
 * `<ComboboxItem>` when the item's value is an empty string, because the empty string
 * is what CLEARS a select and shows its placeholder. The throw happens while the
 * subtree renders, and from then on every update of that subtree dies in Vue's runtime
 * („Cannot read properties of null (reading 'type')“).
 *
 * What that looks like to a hotelier is not an error at all. A dialog that offers such
 * an option cannot be closed — „Shrani“, „Prekliči“, the ✕ and Escape are all updates,
 * and all inert; the record IS written and the modal stays on the screen until the page
 * is reloaded. On the document screen it is worse: the fields that were being drawn
 * when the throw happened simply never appear, so „Izdaja“ could not be written at all
 * on a house that had entered its first cost centre (found by the browser suite,
 * 2026-09-11, `e2e/specs/04-issue.spec.ts`).
 *
 * The sting in both is that the fault arrives with the DATA. A house with an empty
 * codebook hides the option behind a `length > 1` guard and behaves perfectly, so a
 * demonstration hotel never shows it.
 *
 * # The rule
 *
 * A picker's „none“ option carries {@link NONE}, and the sentinel is traded for an
 * empty string at the model boundary — `withNone` on the way in from the service,
 * `withoutNone` on the way out to it. Nothing above this file ever sees the sentinel
 * and nothing below it ever sees an empty option value.
 *
 * `libs/filter.ts` holds the same trick for the other empty value a picker offers,
 * „show everything“. The two are kept apart because they mean opposite things: `ALL`
 * widens a query, `NONE` says a field is unset.
 */
export const NONE = '__none__'

/** A picker's „none“ option, so no screen has to spell the sentinel itself. */
export const noneItem = (label: string): { label: string, value: string } => ({ label, value: NONE })

/** What the picker holds: the service's empty value becomes the sentinel. */
export const withNone = (value: string | null | undefined): string => value || NONE

/** What the service is sent: the sentinel becomes the empty value again. */
export const withoutNone = (value: string | null | undefined): string =>
    (value === NONE || value === null || value === undefined ? '' : value)

/**
 * A picker's model over a field that is stored as an empty string when unset.
 *
 * For the forms whose `v-model` points straight at the payload. Where a screen keeps a
 * draft of its own — a row per category, a line per article — it is simpler to hold the
 * sentinel in the draft and call `withoutNone` where the draft is sent.
 */
export const noneModel = (
    read: () => string | null | undefined,
    write: (value: string) => void,
): WritableComputedRef<string> => computed({
    get: () => withNone(read()),
    set: (value: string) => write(withoutNone(value)),
})
